// /c/{id}/manage: the instructor's class, as /shares is laid out — a card
// inviting students (the join code, Copy code, Copy link), then tabs:
// Assignments (rows, open then archived, Edit and Archive behind a row's ⋯,
// added and edited in a drawer; none, a card saying what they are), Exams
// (the exams assigned to the class, apart from its assignments,
// components/ClassExams, byj-1wv.56.15), Files (what is given to the class,
// components/ClassFiles, RFC-054), Templates (the app templates given to it,
// components/ClassTemplates, RFC-052, byj-1wv.77), Roster
// (the instructor on a line of its own over the students, each one's consent
// a badge on their row, Remove behind its ⋯), Recordings (what the students
// recorded of the assignments, components/ClassRecordings) and Policy (turns,
// recordings and alerts apart, saved with PATCH, what is not saved yet said
// beside Save; under it, deleting the class, byj-56x) (RFC-043 M3,
// byj-1wv.4.3, byj-1wv.51.6). The tab is kept per browser.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, classExamsExportURL, type Assignment, type AssignmentIn, type ClassDetail, type ClassMember, type ClassPolicy } from "../api";
import { AssignmentFields, assignmentIn, draftOf } from "../components/AssignmentFields";
import { ClassExams } from "../components/ClassExams";
import { ClassFiles } from "../components/ClassFiles";
import { ClassRecordings } from "../components/ClassRecordings";
import { ClassTemplates } from "../components/ClassTemplates";
import { Link } from "../components/Nav";
import { Page, PageHead, PageLoading, PageNotice } from "../components/Page";
import { useConfirm } from "../components/Confirm";
import { CopyButton } from "../components/Copy";
import { StateBar } from "../components/StateBar";
import { Drawer } from "../components/Drawer";
import { panelId, tabId, Tabs } from "../components/Tabs";
import { ClassManageTabKey } from "../classManage";
import { useKeptChoice } from "../kept";
import { RowMenu } from "../components/RowMenu";
import { EmptyRows, Grouped, initialOf } from "../components/Rows";
import { navigate } from "../router";
import { t, when, whenMinute, word } from "../i18n";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** A student's consent: a word for its badge, the whole of it for its title, and whether it is the current one. */
function consentStatus(m: ClassMember, current: string): { badge: string; text: string; ok: boolean } {
  if (!m.consent_at || !m.consent_version) return { badge: t("classes:consentNone"), text: t("classes:consentMissing"), ok: false };
  if (m.consent_version !== current) return { badge: t("classes:consentOld"), text: t("classes:consentOutdated", { v: m.consent_version }), ok: false };
  return { badge: t("classes:consentOk"), text: t("classes:consented", { v: m.consent_version, at: when(m.consent_at) }), ok: true };
}

/** What the instructor may do with turns in a student's session, as the policy's two flags hold it. */
type TurnsMode = "never" | "ask" | "consent";
const turnsModes: { mode: TurnsMode; inject: boolean; consent: boolean }[] = [
  { mode: "never", inject: false, consent: false },
  { mode: "ask", inject: true, consent: false },
  { mode: "consent", inject: true, consent: true },
];
/** The choice a policy is: no turns asked for is "never", whatever the second flag says. */
const turnsMode = (p: ClassPolicy): TurnsMode => (!p.inject ? "never" : p.consent_inject ? "consent" : "ask");

function num(v: string, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

type Tab = "assignments" | "exams" | "files" | "templates" | "roster" | "recordings" | "policy";
const tabs: Tab[] = ["assignments", "exams", "files", "templates", "roster", "recordings", "policy"];

const useTab = () => useKeptChoice<Tab>(ClassManageTabKey, "assignments", tabs);

/** An assignment's fields, for adding one or editing one (RFC-043 model 19). */
function AssignmentForm({
  initial,
  withKey,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: AssignmentIn;
  withKey: boolean;
  busy: boolean;
  submitLabel: string;
  onSubmit: (a: AssignmentIn & { clear_due?: boolean }) => void;
  onCancel?: () => void;
}) {
  const [draft, setDraft] = useState(() => draftOf(initial));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!draft.title.trim()) return;
    const a = assignmentIn(draft);
    onSubmit({
      ...a,
      key: withKey ? a.key : undefined,
      // an edit sends what to do even when emptied, which clears it
      text: draft.text.trim(),
      clear_due: !draft.due && !!initial.due_at,
    });
  };
  return (
    <form className="stack" onSubmit={submit} data-testid="assignment-form">
      <AssignmentFields draft={draft} onChange={(p) => setDraft((d) => ({ ...d, ...p }))} withKey={withKey} />
      <div className="row">
        <button className="primary" type="submit" disabled={busy || !draft.title.trim()}>
          {submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="small" onClick={onCancel}>
            {t("classes:cancelEdit")}
          </button>
        )}
      </div>
    </form>
  );
}


/** What the drawer holds: a new assignment's form, or one's to edit. */
type Editing = { kind: "new" } | { kind: "edit"; a: Assignment };

export function ClassManage({ id }: { id: string }) {
  const [cls, setCls] = useState<ClassDetail | null>(null);
  const [policy, setPolicy] = useState<ClassPolicy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useTab();
  // how many recordings the Recordings tab found, once it has looked
  const [recordings, setRecordings] = useState<number | null>(null);
  // and how many exams are assigned to the class (byj-1wv.56.15)
  const [exams, setExams] = useState<number | null>(null);
  // and how many files are given to it (RFC-054 §5)
  const [files, setFiles] = useState<number | null>(null);
  const [templates, setTemplates] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const c = await api.class(id);
      setCls(c);
      setPolicy(c.policy);
    } catch (err) {
      setError(errText(err));
    }
  }, [id]);
  useEffect(() => {
    void load();
  }, [load]);

  if (error && !cls) {
    return (
      <PageNotice error={error}>
        <Link to="/classes">{t("classes:classesTitle")}</Link>
      </PageNotice>
    );
  }
  if (!cls || !policy) return <PageLoading />;
  if (cls.role !== "instructor") {
    return (
      <PageNotice message={t("classes:onlyInstructor")}>
        <Link to={`/c/${encodeURIComponent(cls.id)}`}>{cls.name}</Link>
      </PageNotice>
    );
  }

  const classPath = `/c/${encodeURIComponent(cls.id)}`;
  const code = cls.join_code ?? "";
  const joinLink = `${window.location.origin}/c/join?code=${encodeURIComponent(code)}`;

  const remove = async (m: ClassMember) => {
    if (!(await confirm(t("classes:removeConfirm", { name: m.name }), { action: t("classes:removeMember"), danger: true }))) return;
    try {
      await api.removeMember(cls.id, m.user);
      setCls((c) => (c ? { ...c, members: (c.members ?? []).filter((x) => x.user !== m.user) } : c));
    } catch (err) {
      setError(errText(err));
    }
  };

  // the class gone for good, after a question (byj-56x)
  const deleteClass = async () => {
    if (!(await confirm(t("classes:deleteClassConfirm", { name: cls.name }), { action: t("classes:deleteClass"), danger: true }))) return;
    try {
      await api.deleteClass(cls.id);
      navigate("/classes");
    } catch (err) {
      setError(errText(err));
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      // turns not requested, none run without asking either: what is kept is
      // what is said, and turning requests back on later revives nothing unseen
      const c = await api.updateClassPolicy(cls.id, policy.inject ? policy : { ...policy, consent_inject: false });
      setCls((old) => ({ ...c, members: c.members ?? old?.members }));
      setPolicy(c.policy);
      setSaved(true);
    } catch (err) {
      setError(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const assignments = cls.assignments ?? [];
  const replaceAssignment = (a: Assignment) =>
    setCls((c) => (c ? { ...c, assignments: (c.assignments ?? []).map((x) => (x.key === a.key ? a : x)) } : c));
  const add = async (a: AssignmentIn) => {
    setBusy(true);
    setError(null);
    try {
      const made = await api.addAssignment(cls.id, a);
      setCls((c) => (c ? { ...c, assignments: [...(c.assignments ?? []), made] } : c));
      setEditing(null);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  const update = async (key: string, a: AssignmentIn & { clear_due?: boolean }) => {
    setBusy(true);
    setError(null);
    try {
      replaceAssignment(await api.updateAssignment(cls.id, key, { title: a.title, text: a.text, due_at: a.due_at ?? undefined, clear_due: a.clear_due }));
      setEditing(null);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  const archive = async (a: Assignment) => {
    if (!(await confirm(t("classes:archiveConfirm", { title: a.title }), { action: t("classes:archiveAssignment"), danger: true }))) return;
    try {
      await api.archiveAssignment(cls.id, a.key);
      replaceAssignment({ ...a, archived_at: new Date().toISOString() });
    } catch (err) {
      setError(errText(err));
    }
  };

  const set = (p: Partial<ClassPolicy>) => {
    setSaved(false);
    setPolicy((cur) => (cur ? { ...cur, ...p } : cur));
  };
  const setAlert = (p: Partial<ClassPolicy["alerts"]>) => set({ alerts: { ...policy.alerts, ...p } });
  const members = cls.members ?? [];
  const students = members.filter((m) => m.role === "student");

  const head: Record<Tab, { title: string; count: number | null }> = {
    assignments: { title: t("classes:assignments"), count: assignments.filter((a) => !a.archived_at).length },
    exams: { title: t("exams:classExamsTab"), count: exams },
    files: { title: t("files:classFilesTab"), count: files },
    templates: { title: t("classes:classTemplatesTab"), count: templates },
    roster: { title: t("classes:roster"), count: students.length },
    recordings: { title: t("recordings:recordingsTitle"), count: recordings },
    policy: { title: t("classes:policyFormTitle"), count: null },
  };
  const instructors = members.filter((m) => m.role !== "student");
  const agreed = students.filter((m) => consentStatus(m, cls.consent.version).ok).length;
  // what the form holds that the class does not yet
  const dirty = JSON.stringify(policy) !== JSON.stringify(cls.policy);

  const assignmentRow = (a: Assignment) => (
    <li key={a.key} className={`share-row assignment-row ${a.archived_at ? "is-ended" : "is-live"}`}>
      <span className="row-lead" />
      {/* an assignment's sign, not its key cut to fit: the key is whole on the line under its title */}
      <span className="kind-box" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="5" y="4" width="14" height="17" rx="2" />
          <path d="M9 4.5V3h6v1.5M8.5 10h7M8.5 14h7M8.5 18h4" />
        </svg>
      </span>
      <div className="row-main">
        <Link to={`${classPath}/a/${encodeURIComponent(a.key)}`} className="row-title">
          {a.title}
        </Link>
        <div className="row-sub">
          <code>{a.key}</code> · {a.due_at ? t("classes:dueOn", { at: whenMinute(a.due_at) }) : t("classes:noDue")}
          {a.archived_at && <> · {t("classes:archivedOn", { at: whenMinute(a.archived_at) })}</>}
        </div>
      </div>
      <div className="row-meta" />
      <RowMenu
        label={t("shares:moreFor", { title: a.title })}
        items={[
          { label: t("classes:openAssignmentPage"), onSelect: () => navigate(`${classPath}/a/${encodeURIComponent(a.key)}`) },
          ...(a.archived_at
            ? []
            : [
                { label: t("classes:editAssignment"), onSelect: () => setEditing({ kind: "edit", a }) },
                { label: t("classes:archiveAssignment"), onSelect: () => void archive(a), danger: true },
              ]),
        ]}
      />
    </li>
  );

  const memberRow = (m: ClassMember) => {
    const c = consentStatus(m, cls.consent.version);
    return (
      <li key={m.user} className="share-row member-row is-live">
        <span className="row-lead" />
        <span className="kind-box" aria-hidden="true">
          {initialOf(m.name)}
        </span>
        <div className="row-main">
          <span className="row-title">{m.name}</span>
          {/* what they agreed to and when, on the row: not in a tooltip, which a touch screen cannot show */}
          <div className="row-sub">{c.text}</div>
        </div>
        <div className="row-meta">
          <span className={`chip consent-chip ${c.ok ? "chip-ok" : "chip-warn"}`} data-testid="consent">
            {c.badge}
          </span>
        </div>
        <RowMenu label={t("shares:moreFor", { title: m.name })} items={[{ label: t("classes:removeMember"), onSelect: () => void remove(m), danger: true }]} />
      </li>
    );
  };

  return (
    <Page>
      {confirmDialog}
      <PageHead
        title={t("classes:manageTitle", { name: cls.name })}
        actions={
          <>
            {/* the grades of the exams assigned to the class (RFC-048, byj-1wv.56.6) */}
            <a className="button" href={classExamsExportURL(cls.id)} download>
              {t("exams:exportClassExams")}
            </a>
            <Link to={`${classPath}/preview`} className="button">
              {t("classes:previewAsStudent")}
            </Link>
            <Link to={classPath} className="button">
              {t("classes:dashboard")}
            </Link>
          </>
        }
      />
      {/* the bar under the head, as a share's page has one: the two ways in,
          each with its copy icon beside it, the code large enough to read out */}
      <StateBar
        className="invite-bar"
        label={t("classes:inviteStudents")}
        state="invite"
        stateLabel={<span className="share-bar-state">{t("classes:inviteStudents")}</span>}
        testid="invite"
      >
        <span className="invite-way">
          <span className="invite-label">{t("classes:joinCodeShort")}</span>
          <code className="join-code" data-testid="join-code">
            {code}
          </code>
          <CopyButton icon text={code} label={t("classes:copyCode")} />
        </span>
        <span className="invite-way invite-way-link">
          <span className="invite-label">{t("classes:joinLinkShort")}</span>
          <code className="join-url" data-testid="join-link" title={joinLink}>
            {joinLink.replace(/^https?:\/\//, "")}
          </code>
          <CopyButton icon text={joinLink} label={t("classes:copyLink")} />
        </span>
      </StateBar>
      <p className="muted small invite-help">{t("classes:joinCodeHelp")}</p>
      {error && <p className="error">{t("common:error", { m: error })}</p>}

      <Tabs
        prefix="manage"
        label={t("classes:manageTabs")}
        value={tab}
        onChange={setTab}
        tabs={tabs.map((id) => ({
          id,
          label: (
            <>
              {head[id].title}
              {head[id].count !== null && <span className="tab-count">{head[id].count}</span>}
            </>
          ),
        }))}
      />

      <div role="tabpanel" id={panelId("manage", "assignments")} aria-labelledby={tabId("manage", "assignments")} hidden={tab !== "assignments"} className="rows-panel" data-testid="assignments">
        {assignments.length === 0 ? (
          <div className="empty-card" data-testid="no-assignments">
            <h3 className="flush">{t("classes:noAssignments")}</h3>
            <p className="muted small">{t("classes:noAssignmentsLead", { code })}</p>
            <button className="primary" onClick={() => setEditing({ kind: "new" })}>
              {t("classes:addAssignment")}
            </button>
          </div>
        ) : (
          <Grouped
            id="assignments"
            groups={[
              {
                title: t("classes:openGroup"),
                items: assignments.filter((a) => !a.archived_at),
                // every one archived, Open still stands with Add on its line,
                // and the archived rows under their own heading
                none: <EmptyRows text={t("classes:noOpenAssignments")} />,
                actions: (
                  <button className="primary" onClick={() => setEditing({ kind: "new" })}>
                    {t("classes:addAssignment")}
                  </button>
                ),
              },
              { title: t("classes:archivedGroup"), items: assignments.filter((a) => a.archived_at) },
            ]}
            row={assignmentRow}
          />
        )}
      </div>

      <div role="tabpanel" id={panelId("manage", "exams")} aria-labelledby={tabId("manage", "exams")} hidden={tab !== "exams"} className="rows-panel" data-testid="exams">
        <ClassExams cls={cls} onCount={setExams} />
      </div>

      <div role="tabpanel" id={panelId("manage", "files")} aria-labelledby={tabId("manage", "files")} hidden={tab !== "files"} className="rows-panel" data-testid="files">
        <ClassFiles cls={cls} onCount={setFiles} />
      </div>

      <div role="tabpanel" id={panelId("manage", "templates")} aria-labelledby={tabId("manage", "templates")} hidden={tab !== "templates"} className="rows-panel" data-testid="templates">
        <ClassTemplates cls={cls} onCount={setTemplates} />
      </div>

      <div role="tabpanel" id={panelId("manage", "roster")} aria-labelledby={tabId("manage", "roster")} hidden={tab !== "roster"} className="rows-panel" data-testid="roster">
        {/* the instructor a line over the students, and how many have agreed */}
        <p className="muted small roster-summary" data-testid="roster-summary">
          {instructors.length > 0 && <>{t("classes:instructorLine", { names: instructors.map((m) => m.name).join(", ") })} · </>}
          {t("classes:rosterSummary", { count: students.length, agreed })}
        </p>
        {students.length === 0 ? (
          <div className="empty-card">
            <h3 className="flush">{t("classes:noMembers")}</h3>
            <p className="muted small">{t("classes:noMembersLead")}</p>
          </div>
        ) : (
          <ul className="rows">{students.map(memberRow)}</ul>
        )}
      </div>

      <div role="tabpanel" id={panelId("manage", "recordings")} aria-labelledby={tabId("manage", "recordings")} hidden={tab !== "recordings"} className="rows-panel" data-testid="recordings">
        <ClassRecordings cls={cls} onCount={setRecordings} />
      </div>

      <div role="tabpanel" id={panelId("manage", "policy")} aria-labelledby={tabId("manage", "policy")} hidden={tab !== "policy"}>
        <form className="policy-form stack" onSubmit={(e) => void save(e)}>
          <section className="policy-group" aria-labelledby="policy-turns">
            <h3 id="policy-turns">{t("classes:policyTurns")}</h3>
            {/* one choice of three, not two switches: whether turns are asked
                for at all and whether a student approves each are one thing,
                so no choice says turns run unasked while none may be asked */}
            <fieldset className="turns-choice">
              <legend className="turns-lead">{t("classes:policyTurnsLead")}</legend>
              {turnsModes.map((m) => (
                <label key={m.mode} className={`turns-option${turnsMode(policy) === m.mode ? " is-chosen" : ""}`}>
                  <input type="radio" name="turns" checked={turnsMode(policy) === m.mode} onChange={() => set({ inject: m.inject, consent_inject: m.consent })} />
                  <span>
                    <strong>{word("classes:turnsMode", m.mode)}</strong>
                    <span className="muted small"> — {word("classes:turnsModeHint", m.mode)}</span>
                  </span>
                </label>
              ))}
            </fieldset>
          </section>
          <section className="policy-group" aria-labelledby="policy-recordings">
            <h3 id="policy-recordings">{t("classes:policyRecordings")}</h3>
            <NumberRow label={t("classes:retentionLabel")} unit={t("classes:unitDays")} value={policy.retention_days} onChange={(v) => set({ retention_days: num(v, policy.retention_days) })} />
          </section>
          <section className="policy-group" aria-labelledby="policy-alerts">
            <h3 id="policy-alerts">{t("classes:alertThresholds")}</h3>
            <NumberRow label={t("classes:permissionWaitLabel")} unit={t("classes:unitSeconds")} value={policy.alerts.permission_wait_s} onChange={(v) => setAlert({ permission_wait_s: num(v, policy.alerts.permission_wait_s) })} />
            <NumberRow label={t("classes:costSpikeLabel")} unit={t("classes:unitTimes")} step="0.5" value={policy.alerts.cost_spike_x} onChange={(v) => setAlert({ cost_spike_x: num(v, policy.alerts.cost_spike_x) })} />
            <NumberRow label={t("classes:idleLabel")} unit={t("classes:unitMinutes")} value={policy.alerts.idle_min} onChange={(v) => setAlert({ idle_min: num(v, policy.alerts.idle_min) })} />
          </section>
          <div className="row policy-save">
            <button className="primary" type="submit" disabled={saving || !dirty}>
              {saving ? t("common:saving") : t("common:save")}
            </button>
            {dirty ? (
              <span className="warn small" data-testid="unsaved">
                {t("classes:unsaved")}
              </span>
            ) : (
              saved && (
                <span className="ok-text small" role="status">
                  {t("common:saved")}
                </span>
              )
            )}
          </div>
        </form>
        {/* the class gone for good, after a question (byj-56x): apart from the
            policy, which Save does not reach */}
        <section className="policy-group delete-class" aria-labelledby="delete-class-title">
          <h3 id="delete-class-title">{t("classes:deleteClassTitle")}</h3>
          <p className="muted small flush">{t("classes:deleteClassHelp")}</p>
          <div className="row">
            <button className="danger" onClick={() => void deleteClass()}>
              {t("classes:deleteClass")}
            </button>
          </div>
        </section>
      </div>

      <Drawer
        id="assignment-drawer"
        title={editing?.kind === "edit" ? t("classes:editAssignmentHeading", { title: editing.a.title }) : t("classes:newAssignmentHeading")}
        open={editing !== null}
        onClose={() => setEditing(null)}
      >
        {editing?.kind === "new" && (
          <AssignmentForm initial={{ title: "", key: "", text: "" }} withKey busy={busy} submitLabel={busy ? t("classes:adding") : t("classes:addAssignment")} onSubmit={(x) => void add(x)} onCancel={() => setEditing(null)} />
        )}
        {editing?.kind === "edit" && (
          <AssignmentForm
            key={editing.a.key}
            initial={{ key: editing.a.key, title: editing.a.title, text: editing.a.text, due_at: editing.a.due_at }}
            withKey={false}
            busy={busy}
            submitLabel={t("classes:saveAssignment")}
            onSubmit={(x) => void update(editing.a.key, x)}
            onCancel={() => setEditing(null)}
          />
        )}
      </Drawer>
    </Page>
  );
}

/** A number the policy holds: what it is on the left, the field and its unit in the column every row's control stands in. */
function NumberRow({ label, unit, value, step, onChange }: { label: string; unit: string; value: number; step?: string; onChange: (v: string) => void }) {
  return (
    <label className="setting-row num-row">
      <span className="setting-text">{label}</span>
      <span className="setting-control num-field">
        <input type="number" min={1} step={step} value={value} onChange={(e) => onChange(e.target.value)} />
        <span className="muted small">{unit}</span>
      </span>
    </label>
  );
}
